/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IAppState,
  IApplicationPlayerScreenModel,
  IMediaModel,
  MediaHelper,
  MediaType,
  ScreenType,
  ServerTimeStore,
  TimeHelper,
  useMediaByIdSelector,
  useSelector,
  useScreenConfiguration,
} from "@bms/common";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IMediaButtonProps, MediaButton } from "./MediaButton";

dayjs.extend(relativeTime);

export interface ILiveWatchButtonProps extends IMediaButtonProps {
  media?: IMediaModel;
}

export const LiveWatchButton = ({
  media,
  children,
  ...restProps
}: ILiveWatchButtonProps) => {
  const { t } = useTranslation();
  const playerScreen = useScreenConfiguration<IApplicationPlayerScreenModel>(
    ScreenType.Player,
  );
  const isConfigurationLoading = useSelector(
    (state: IAppState) => state.configuration.isLoading,
  );
  const mediaState = useMediaByIdSelector(media?.Id ?? "");
  const serverTime = useSelector((state: IAppState) => state.serverTime);
  const [, setUpdateButton] = useState(0);
  const serverTimeMs =
    ServerTimeStore.Selectors.getCurrentServerTimeMs(serverTime);
  const currentDatetime =
    serverTimeMs !== undefined ? dayjs(serverTimeMs) : undefined;
  const preLiveAccessTime = Math.max(
    playerScreen.LiveStreaming?.PreLiveAccessTime ?? 0,
    0,
  );

  const millisToMinutes = (millis: number) => {
    const minutes = Math.floor(millis / 60000);
    return minutes;
  };
  const compareTimeValuesBeforeMovieStarted =
    currentDatetime?.diff(media?.StartDateTime) ?? Number.NEGATIVE_INFINITY;
  const compareTimeValuesAfterMovieEnded =
    currentDatetime?.diff(media?.EndDateTime) ?? Number.NEGATIVE_INFINITY;

  const updateButtonFrequencyBeforeEventStarted =
    millisToMinutes(compareTimeValuesBeforeMovieStarted) >
      -Math.max(preLiveAccessTime + 1, 45) &&
    millisToMinutes(compareTimeValuesBeforeMovieStarted) <= 1;

  const updateButtonFrequencyAfterEventEnded =
    millisToMinutes(compareTimeValuesAfterMovieEnded) >= 0 &&
    millisToMinutes(compareTimeValuesAfterMovieEnded) <= 45;

  useEffect(() => {
    let intervalID: NodeJS.Timeout | undefined;
    if (
      updateButtonFrequencyBeforeEventStarted ||
      updateButtonFrequencyAfterEventEnded
    ) {
      intervalID = setInterval(() => {
        setUpdateButton((prevState) => prevState + 1);
      }, 30 * 1000);
    }
    return () => {
      if (intervalID) {
        clearInterval(intervalID);
      }
    };
  }, [
    updateButtonFrequencyAfterEventEnded,
    updateButtonFrequencyBeforeEventStarted,
  ]);

  const isScheduledMedia = MediaHelper.isScheduledLiveScreenMediaType(
    media?.MediaTypeCode,
  );
  const isBeforeLiveStart = Boolean(
    media?.StartDateTime &&
      currentDatetime?.isBefore(dayjs(media.StartDateTime)),
  );
  const isPastEventReplayAvailable = Boolean(
    (media?.MediaTypeCode === MediaType.Match ||
      media?.MediaTypeCode === MediaType.Event) &&
      media?.EndDateTime &&
      currentDatetime?.isAfter(dayjs(media.EndDateTime)) &&
      media.PlayableMediaId &&
      media.PlayableMediaTypeCode === MediaType.Video,
  );

  const blockedButtonText = currentDatetime
    ? TimeHelper.formatRelativeDateInRange(
        t("LIVE__BROADCAST_WILL_START"),
        t("LIVE__BROADCAST_ENDED"),
        media?.StartDateTime,
        media?.EndDateTime,
        currentDatetime.toDate(),
      )
    : t("LIVE__BROADCAST_WILL_START");

  const blockedButton = (
    <MediaButton variant="primary" disabled>
      {blockedButtonText}
    </MediaButton>
  );

  if (
    isScheduledMedia &&
    !isPastEventReplayAvailable &&
    media?.StartDateTime &&
    !currentDatetime
  ) {
    return blockedButton;
  }

  if (
    isScheduledMedia &&
    !isPastEventReplayAvailable &&
    media?.StartDateTime &&
    isBeforeLiveStart &&
    (mediaState.IsLoading || isConfigurationLoading)
  ) {
    return blockedButton;
  }

  const isLiveStatePending = Boolean(
    isScheduledMedia &&
      !isPastEventReplayAvailable &&
      (mediaState.IsLoading || !media?.StartDateTime),
  );

  if (isLiveStatePending) {
    return null;
  }

  const isLivePreAccessEnabled = Boolean(
    isScheduledMedia &&
      currentDatetime &&
      playerScreen.LiveStreaming?.EnablePreLiveAccess === true &&
      media?.StartDateTime &&
      currentDatetime.isBefore(dayjs(media.StartDateTime)) &&
      (currentDatetime.isAfter(
        dayjs(media.StartDateTime).subtract(preLiveAccessTime, "minute"),
      ) ||
        currentDatetime.isSame(
          dayjs(media.StartDateTime).subtract(preLiveAccessTime, "minute"),
        )),
  );

  if (
    (media?.StartDateTime &&
      currentDatetime?.isBefore(dayjs(media?.StartDateTime)) &&
      !isLivePreAccessEnabled) ||
    (media?.EndDateTime && currentDatetime?.isAfter(dayjs(media?.EndDateTime)))
  ) {
    if (
      (media?.MediaTypeCode === MediaType.Match ||
        media?.MediaTypeCode === MediaType.Event) &&
      media?.EndDateTime &&
      currentDatetime?.isAfter(dayjs(media?.EndDateTime)) &&
      media.PlayableMediaId &&
      media?.PlayableMediaTypeCode === MediaType.Video
    ) {
      return <MediaButton {...restProps}>{children}</MediaButton>;
    }

    return blockedButton;
  } else {
    return <MediaButton {...restProps}>{children}</MediaButton>;
  }
};
